import { useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Pressable,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  Check,
  CheckCircle2,
  CreditCard,
  MessageCircle,
  Pencil,
  PiggyBank,
} from "lucide-react-native";
import { SoftSecondaryButton, Tag, type TagTone } from "@louise/moon-ui";
import { formatCurrency } from "@src/packages/i18n";
import { useSpendingAccountBalances } from "@src/packages/costs/hooks/useSpendingAccountBalances";
import { EMBER } from "../../onboarding/theme";
import { GLASS_CARD_STYLE, colors } from "@louise/moon-ui/theme/tokens";
import CostsCardTabs from "./CostsCardTabs";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type SpendingAccount = "HSA" | "FSA";

type SpendingCategory = {
  title: string;
  detail: string;
  status: "Often eligible" | "May qualify" | "Ask first";
};

const SPENDING_ACCOUNT_DETAILS: Record<
  SpendingAccount,
  {
    title: string;
    intro: string;
    timing: string;
    categories: SpendingCategory[];
  }
> = {
  HSA: {
    title: "Health Savings Account",
    intro:
      "Use your health savings account dollars for qualified care by keeping a record of what you paid.",
    timing:
      "HSA funds usually roll over year to year, and eligibility depends on your plan and tax rules.",
    categories: [
      {
        title: "Fertility diagnostics",
        detail: "Bloodwork, ultrasounds, semen analysis, and other workups.",
        status: "Often eligible",
      },
      {
        title: "Treatment and medication",
        detail: "IVF or IUI care and prescribed fertility medications.",
        status: "Often eligible",
      },
      {
        title: "Egg or sperm storage",
        detail: "Temporary storage connected to fertility treatment.",
        status: "May qualify",
      },
      {
        title: "Travel, donor, or surrogacy costs",
        detail: "Ask your administrator before counting these as eligible.",
        status: "Ask first",
      },
    ],
  },
  FSA: {
    title: "Flexible Spending Account",
    intro:
      "Use FSA dollars for eligible care before your plan’s deadline, with your administrator’s rules in mind.",
    timing:
      "Carryover and grace-period rules depend on your employer’s plan, and eligibility depends on your plan and tax rules.",
    categories: [
      {
        title: "Fertility diagnostics",
        detail: "Bloodwork, ultrasounds, semen analysis, and other workups.",
        status: "Often eligible",
      },
      {
        title: "Treatment and medication",
        detail: "IVF or IUI care and prescribed fertility medications.",
        status: "Often eligible",
      },
      {
        title: "Egg or sperm storage",
        detail: "Temporary storage connected to fertility treatment.",
        status: "May qualify",
      },
      {
        title: "Travel, donor, or surrogacy costs",
        detail: "Ask your administrator before counting these as eligible.",
        status: "Ask first",
      },
    ],
  },
};

const hsaFsaIllustration =
  require("@src/apps/moon/assets/images/hsa-fsa-savings-illustration.png") as number;

function StatusPill({ label, tone }: { label: string; tone?: TagTone }) {
  const resolvedTone: TagTone =
    tone ??
    (label === "Often eligible"
      ? "green"
      : label === "May qualify"
        ? "gold"
        : label === "Ask first"
          ? "coral"
          : "neutral");

  return <Tag label={label} tone={resolvedTone} shape="pill" />;
}

function getSpendingCategoryColor(status: SpendingCategory["status"]) {
  if (status === "Often eligible") return EMBER.colors.green;
  if (status === "May qualify") return EMBER.colors.gold;
  return EMBER.colors.coral;
}

type Props = {
  locale: Parameters<typeof formatCurrency>[1];
  onOpenChat: (account: SpendingAccount) => void;
};

export default function SpendingAccountCard({ locale, onOpenChat }: Props) {
  const [activeAccount, setActiveAccount] = useState<SpendingAccount>("HSA");
  const {
    data: persistedBalances = [],
    saveBalance: persistBalance,
    isSaving,
  } = useSpendingAccountBalances();
  const balances = useMemo<Record<SpendingAccount, string>>(
    () => ({
      HSA:
        persistedBalances.find((balance) => balance.accountType === "hsa")
          ?.balanceCents != null
          ? (
              persistedBalances.find(
                (balance) => balance.accountType === "hsa",
              )!.balanceCents / 100
            ).toFixed(2)
          : "",
      FSA:
        persistedBalances.find((balance) => balance.accountType === "fsa")
          ?.balanceCents != null
          ? (
              persistedBalances.find(
                (balance) => balance.accountType === "fsa",
              )!.balanceCents / 100
            ).toFixed(2)
          : "",
    }),
    [persistedBalances],
  );
  const [balanceDraft, setBalanceDraft] = useState("");
  const capturePress = usePressCapture("SpendingAccountCard");
  const [isEditingBalance, setIsEditingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const [balanceWidth, setBalanceWidth] = useState(0);
  const pigRotation = useRef(new Animated.Value(0)).current;
  const pigRotationTarget = useRef(0);

  const details = SPENDING_ACCOUNT_DETAILS[activeAccount];
  const savedBalance = balances[activeAccount];
  const formattedBalance = savedBalance
    ? formatCurrency(Number(savedBalance), locale, { fractionDigits: 2 })
    : formatCurrency(0, locale, { fractionDigits: 2 });

  const changeAccount = (account: SpendingAccount) => {
    if (account === activeAccount) return;

    pigRotationTarget.current += 180;
    Animated.timing(pigRotation, {
      toValue: pigRotationTarget.current,
      duration: 520,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
    setActiveAccount(account);
    setBalanceDraft(balances[account]);
    setIsEditingBalance(false);
    setBalanceError(null);
    setShowCategories(false);
  };

  const openBalanceEditor = () => {
    setBalanceDraft(savedBalance);
    setBalanceError(null);
    setIsEditingBalance(true);
  };

  const saveBalance = () => {
    const normalized = balanceDraft.replace(/[,$\s]/g, "");
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      setBalanceError("Enter a balance such as 1250 or 1250.50.");
      return;
    }

    setBalanceError(null);
    void persistBalance({
      accountType: activeAccount.toLowerCase() as "hsa" | "fsa",
      balanceCents:
        Number(normalized.split(".")[0]) * 100 +
        Number(`${normalized.split(".")[1] ?? ""}00`.slice(0, 2)),
    })
      .then(() => setIsEditingBalance(false))
      .catch((error: unknown) => {
        setBalanceError(
          error instanceof Error
            ? error.message
            : "We could not save that balance. Try again.",
        );
      });
  };

  return (
    <View
      style={{
        overflow: "visible",
        zIndex: 100,
        elevation: 100,
      }}
    >
      <CostsCardTabs
        active={activeAccount}
        onChange={changeAccount}
        options={[
          { value: "HSA", label: "HSA", icon: PiggyBank },
          { value: "FSA", label: "FSA", icon: CreditCard },
        ]}
      />

      <View
        style={{
          ...(GLASS_CARD_STYLE as object),
          padding: 18,
          gap: 16,
          overflow: "visible",
        }}
      >
        <View style={{ alignItems: "center", gap: 7, paddingTop: 2 }}>
          <Animated.Image
            source={hsaFsaIllustration}
            accessibilityLabel="3D piggy bank with a medical cross and benefits card"
            resizeMode="contain"
            style={{
              width: 76,
              height: 64,
              opacity: 0.82,
              transform: [
                { perspective: 700 },
                {
                  rotateY: pigRotation.interpolate({
                    inputRange: [0, 180],
                    outputRange: ["0deg", "180deg"],
                  }),
                },
              ],
            }}
          />
          <View
            style={{
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 48,
              width: "100%",
            }}
          >
            {isEditingBalance ? (
              <TextInput
                autoFocus
                testID={`${activeAccount.toLowerCase()}-balance-input`}
                accessibilityLabel={`${activeAccount} balance amount`}
                value={balanceDraft}
                onChangeText={(value) => {
                  setBalanceDraft(value.replace(/[^0-9.,]/g, ""));
                  if (balanceError) setBalanceError(null);
                }}
                onSubmitEditing={saveBalance}
                onLayout={() => setBalanceWidth(190)}
                placeholder="0.00"
                placeholderTextColor={EMBER.colors.textFaint}
                keyboardType="decimal-pad"
                returnKeyType="done"
                style={{
                  width: 190,
                  minHeight: 48,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: balanceError
                    ? `${EMBER.colors.coral}70`
                    : `${EMBER.colors.textMid}40`,
                  backgroundColor: "rgba(255,255,255,0.42)",
                  paddingHorizontal: 10,
                  color: colors.greyBrown,
                  fontSize: 29,
                  lineHeight: 32,
                  fontWeight: "700",
                  fontFamily: "DMMono_400Regular",
                  textAlign: "center",
                }}
              />
            ) : (
              <Text
                className="font-dm-mono"
                onLayout={(event) =>
                  setBalanceWidth(event.nativeEvent.layout.width)
                }
                style={{
                  fontSize: formattedBalance.length > 14 ? 24 : 29,
                  lineHeight: 32,
                  fontWeight: "700",
                  color: colors.greyBrown,
                  textAlign: "center",
                }}
              >
                {formattedBalance}
              </Text>
            )}
            <Pressable
              testID={
                isEditingBalance
                  ? `${activeAccount.toLowerCase()}-balance-save`
                  : `${activeAccount.toLowerCase()}-balance-edit`
              }
              accessibilityRole="button"
              accessibilityLabel={`${isEditingBalance ? "Save" : "Edit"} ${activeAccount} balance`}
              onPress={() => {
                // Icon-only control; its accessibility label is the copy.
                capturePress(
                  pressLabel(
                    `${isEditingBalance ? "Save" : "Edit"} ${activeAccount} balance`,
                  ),
                  isEditingBalance ? "save_balance" : "edit_balance",
                );
                if (isEditingBalance) saveBalance();
                else openBalanceEditor();
              }}
              disabled={isSaving}
              hitSlop={8}
              style={({ pressed }) => ({
                position: "absolute",
                left: "50%",
                marginLeft:
                  (balanceWidth || (isEditingBalance ? 190 : 0)) / 2 + 8,
                width: 20,
                height: 20,
                borderRadius: 10,
                alignItems: "center",
                justifyContent: "center",
                opacity: pressed ? 0.7 : 0.45,
              })}
            >
              {isEditingBalance ? (
                <Check size={13} color={colors.greyBrown} strokeWidth={2.4} />
              ) : (
                <Pencil size={12} color={colors.greyBrown} strokeWidth={2.1} />
              )}
            </Pressable>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              maxWidth: 330,
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              textAlign: "center",
            }}
          >
            {details.intro} {details.timing}
          </Text>
          {balanceError ? (
            <Text
              accessibilityRole="alert"
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.coral }}
            >
              {balanceError}
            </Text>
          ) : null}
        </View>

        <View style={{ flexDirection: "row", gap: 8 }}>
          <SoftSecondaryButton
            label={
              showCategories ? "Hide eligible expenses" : "Eligible expenses"
            }
            onPress={() => setShowCategories((visible) => !visible)}
            leading={
              <CheckCircle2
                size={16}
                color={EMBER.colors.textMid}
                strokeWidth={1.8}
              />
            }
            flex={1}
          />
          <SoftSecondaryButton
            label="How to use it"
            onPress={() => onOpenChat(activeAccount)}
            leading={
              <MessageCircle
                size={16}
                color={EMBER.colors.textMid}
                strokeWidth={1.8}
              />
            }
            flex={1}
          />
        </View>

        {showCategories ? (
          <View style={{ gap: 9 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              Start with these expense categories
            </Text>
            {details.categories.map((category) => (
              <View
                key={category.title}
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 9,
                }}
              >
                <CheckCircle2
                  size={15}
                  color={getSpendingCategoryColor(category.status)}
                  strokeWidth={1.7}
                  style={{ marginTop: 2 }}
                />
                <View style={{ flex: 1, gap: 2 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        flex: 1,
                        fontSize: 13,
                        fontWeight: "700",
                        color: EMBER.colors.text,
                      }}
                    >
                      {category.title}
                    </Text>
                    <StatusPill label={category.status} />
                  </View>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      lineHeight: 16,
                      color: EMBER.colors.textLight,
                    }}
                  >
                    {category.detail}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </View>
  );
}
